/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import _ from 'lodash';
import React, { PureComponent, ReactComponentElement } from 'react';
import { Chart, ChartConfiguration, ChartTypeRegistry, registerables } from 'chart.js';
import $ from 'jquery';

// ours
import ReportDrawer from './ReportDrawer';
import LiteralsService from '../../services/literals/LiteralsService';
import NavigationService from '../../services/navigation.service';
import { ReportViewModel } from 'src/types/view-models/report.view.model';
import { IconActivity } from '@americanexpress/dls-react';
import Anchor from '../atomic/atoms/anchor/Anchor';
import TransactionOverview from '../atomic/organisms/transaction/TransactionOverview';
import Section from '../section/Section';
import { BudgetModel } from 'src/types/budget.model';
import FormatService from '../../services/format.service';
import ReportBar from '../report-progress-bar/ReportBar';
import { BaseExpenseModel, BaseIncomeModel, ExpensesModel, IncomeReportModel } from 'src/types/income-expense.model';
import { AdjustmentModel } from 'src/types/adjusment.model';
import ReportService, { PlotPoint } from '../../services/report.service';
import { TimePeriod } from 'src/types/time-period';
import { ReportType } from 'src/types/report-type.enum';
import { TransactionModel } from 'src/types/transaction.model';
import { AccountModel } from 'src/types/account.model';
import { SubExpense } from '../report/SubExpense';
import { BudgetCallout } from '../budget-callout/BudgetCallout';

// styles
import styles from './IncomeExpenseDrawer.module.scss';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import DateUtils from 'src/utils/date';
import Selectors from 'src/utils/selectors';

interface LineChartColor {
    lineColor: string;
    backgroundColor: string;
}

export default class IncomeExpenseDrawer extends PureComponent<ReportViewModel> {
    public initialised: boolean = false;
    public chart: Chart;

    constructor(props: ReportViewModel) {
        super(props);
        Chart.register(...registerables);
    }

    public override componentDidUpdate(prevProps: ReportViewModel): void {
        if (this.props.drawerProps.zoom === 'month' || this.props.drawerProps.period !== TimePeriod.PAST) return;
        const prevDrawerProps = _.get(prevProps, 'drawerProps');
        const currentDrawerProps = _.get(this, 'props.drawerProps');
        if (this.initialised && _.isEqual(prevDrawerProps, currentDrawerProps)) return;
        this.initialised = true;
        if (this.chart) this.chart.destroy();
        this.chart = this.getChart();
    }

    public override render(): ReactComponentElement<any> {
        const notPast: boolean = this.props.drawerProps.period !== TimePeriod.PAST;
        const notFuture: boolean = this.props.drawerProps.period !== TimePeriod.FUTURE;

        // income
        const incomeTitle: string = LiteralsService.getLiteral('IncomeExpenseDrawer.incomeTitle', [notPast]);
        const incomeBars: JSX.Element[] = this.getIncomeBars();

        // expenses
        const expenseTitle: string = LiteralsService.getLiteral('IncomeExpenseDrawer.expensesTitle', [notPast]);
        const expenseTotal: number = _.get(this, 'props.drawerProps.expenseTotal', 0);
        const expenseBars: JSX.Element[] = this.getExpenseBars();

        // top deposits and top expenses
        const topDeposits: JSX.Element[] = this.getTopDeposits();
        const showTopDeposits: boolean = topDeposits?.length > 0 && notFuture;
        const topExpenses: JSX.Element[] = this.getTopDeposits(true);
        const showTopExpenses: boolean = topExpenses?.length > 0 && notFuture;

        const showChart: boolean =
            this.props.drawerProps.zoom === 'year' &&
            this.props.drawerProps.period === TimePeriod.PAST &&
            (incomeBars?.length !== 0 || expenseBars?.length !== 0);

        return (
            <ReportDrawer
                drawerProps={this.props.drawerProps}
                title={LiteralsService.getLiteral('IncomeExpenseDrawer.title', [this.props.drawerProps.dateString])}
                reportType={ReportType.INCOME_AND_EXPENSE}
            >
                {showChart && (
                    <div className={styles.chartFlow}>
                        <Typography className={styles.chartTitle} variant={TypographyVariants.HEADING_XS}>
                            {LiteralsService.getLiteral('IncomeExpenseDrawer.cashFlowChartTitle')}
                        </Typography>
                        <canvas data-cy={Selectors.PlanAhead.CHART} id="cashFlowChart" />
                    </div>
                )}
                {incomeBars?.length > 0 && (
                    <Section
                        title={incomeTitle}
                        data-cy={Selectors.PlanAhead.INCOME_SECTION}
                        tooltipDescription={LiteralsService.getLiteral('IncomeExpenseDrawer.incomeTooltip', [notPast])}
                        value={this.props.drawerProps.cashFlowDetails.incomes.amount}
                    >
                        {incomeBars}
                    </Section>
                )}
                {showTopDeposits && (
                    <Section
                        title={LiteralsService.getLiteral('IncomeExpenseDrawer.topDepositsTitle', [notPast])}
                        data-cy={Selectors.PlanAhead.TOP_DEPOSITS_SECTION}
                        tooltipDescription={LiteralsService.getLiteral('IncomeExpenseDrawer.topDepositsTooltip')}
                        extraComp={this.getSeeAll(false)}
                        borderChildComp
                    >
                        {topDeposits}
                    </Section>
                )}
                {expenseBars?.length > 0 ? (
                    <Section
                        title={expenseTitle}
                        data-cy={Selectors.PlanAhead.EXPENSES_SECTION}
                        tooltipDescription={LiteralsService.getLiteral('IncomeExpenseDrawer.expensesTooltip', [
                            notPast
                        ])}
                        value={expenseTotal}
                    >
                        {expenseBars}
                    </Section>
                ) : null}
                {showTopExpenses && (
                    <Section
                        title={LiteralsService.getLiteral('IncomeExpenseDrawer.topExpensesTitle', [notPast])}
                        data-cy={Selectors.PlanAhead.TOP_EXPENSES_SECTION}
                        tooltipDescription={LiteralsService.getLiteral('IncomeExpenseDrawer.topExpensesTooltip')}
                        extraComp={this.getSeeAll(true)}
                        borderChildComp
                    >
                        {topExpenses}
                    </Section>
                )}
            </ReportDrawer>
        );
    }

    private getChart(): Chart {
        let labels: string[];
        const incomeColors: LineChartColor = {
            lineColor: 'rgba(87, 111, 181, 1)',
            backgroundColor: 'rgba(87, 111, 181, 0.1)'
        };
        const expenseColors: LineChartColor = {
            lineColor: 'rgba(0, 57, 148, 1)',
            backgroundColor: 'rgba(0, 57, 148, 0.1)'
        };

        if (this.props.drawerProps.zoom === 'month') {
            labels = ['1', '7', '15', '24', '30'];
        } else if (this.props.drawerProps.zoom === 'year') {
            labels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
        }

        const data = {
            labels,
            datasets: [
                {
                    label: 'Income',
                    borderColor: incomeColors.lineColor,
                    pointBackgroundColor: incomeColors.lineColor,
                    data: this.props.drawerProps.plotPoints.map((pp: PlotPoint) => pp.income),
                    fill: {
                        target: 'origin',
                        above: incomeColors.backgroundColor,
                        below: incomeColors.backgroundColor
                    }
                },
                {
                    label: 'Expenses',
                    borderColor: expenseColors.lineColor,
                    pointBackgroundColor: expenseColors.lineColor,
                    data: this.props.drawerProps.plotPoints.map((pp: PlotPoint) => pp.expense),
                    fill: {
                        target: 'origin',
                        above: expenseColors.backgroundColor,
                        below: expenseColors.backgroundColor
                    }
                }
            ]
        };

        const config = {
            type: 'line',
            data,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        borderRadius: 200,
                        display: true,
                        align: 'end',
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            boxWidth: 6
                        }
                    },
                    filler: {
                        propagate: true
                    }
                },
                elements: {
                    point: {
                        radius: 0
                    }
                },
                scales: {
                    x: {
                        grid: {
                            display: false
                        }
                    },
                    y: {
                        display: false,
                        grid: {
                            display: false
                        }
                    }
                }
            }
        };

        try {
            const canvas: HTMLCanvasElement = document.getElementById('cashFlowChart') as HTMLCanvasElement;
            const ctx: CanvasRenderingContext2D = canvas.getContext('2d');

            return new Chart(ctx, config as ChartConfiguration<keyof ChartTypeRegistry, number[], string>);
        } catch (e) {
            console.error(e);
        }
    }

    // TODO : we really need bases to be strongly typed
    private getExpenseBarChildren(children: any[], color: string, containerClass: string): JSX.Element {
        if (!children) return null;

        return (
            <div
                className={[styles.expenseBarChildrenContainer, containerClass].join(' ')}
                data-cy="expenseBarChildrenContainer"
            >
                <div className={styles.leftContainer}>
                    <div className={styles.childBookmark} style={{ backgroundColor: color }} />
                </div>
                <div className={styles.rightContainer}>
                    {children.map((child: any, index: number) => {
                        const relevantBudget: BudgetModel = this.props.drawerProps.budgets.find(
                            (budget: BudgetModel) => budget.groupId === child.category.id
                        );
                        const onClick: () => void =
                            this.props.drawerProps.period !== TimePeriod.FUTURE
                                ? () => {
                                      void NavigationService.goTo(
                                          this.props.drawerProps.history,
                                          `/drawer/transactions?start=${this.props.drawerProps.startDate}&end=${this.props.drawerProps.endDate}&group=${child.category.id}`,
                                          'Transactions Drawer'
                                      );
                                  }
                                : () => {};

                        return (
                            <SubExpense
                                name={child.name}
                                value={FormatService.formatCurrency(child.amount)}
                                period={this.props.drawerProps.period}
                                onClick={onClick}
                                key={`expense-child-item-${index}`}
                                percentage={FormatService.formatPercentage(child.percentage)}
                            >
                                {this.getBudgetRow(relevantBudget)}
                            </SubExpense>
                        );
                    })}
                </div>
            </div>
        );
    }

    private getExpenseBar(group: AdjustmentModel, adjustments?: AdjustmentModel[]): JSX.Element {
        let colorMap: Map<string, string> = new Map<string, string>();
        const key: string = `expense-bar-${group.accountId}`;
        let relevantBudget: BudgetModel;
        let toDateRow: JSX.Element;
        let totalAmount: number = 0;
        // if this is not a card
        if (!group.detail)
            relevantBudget = this.props.drawerProps.budgets?.find(
                (budget: BudgetModel) => budget.groupId === group.accountId
            );

        const base: BaseExpenseModel[] = group?.children?.filter((child: BaseExpenseModel) => child.actual);
        if (this.props.drawerProps.period === TimePeriod.PRESENT && !DateUtils.isLastMonthOfYear()) {
            group.children = group?.children?.filter((child: BaseExpenseModel) => !child.actual);
        }
        base?.forEach((b: BaseExpenseModel) => {
            totalAmount += b.amount;
        });

        const childContainerClass: string = `expense-bar-children-container-${group.accountId}`;
        const $childContainer: JQuery = $(`.${childContainerClass}`);
        const childToggle: JSX.Element = !_.isEmpty(group.children) && $childContainer && (
            <div className={styles.childToggle} data-cy={Selectors.PlanAhead.SHOW_CATEGORIES_TOGGLE}>
                <Typography
                    onClick={(e) => {
                        const $el = $(e.target);
                        const isVisible: boolean = $childContainer.is(':visible');
                        if (isVisible) {
                            // the subcategories are already visible
                            // this click action should then hide the subcategories
                            $childContainer.css('display', 'none');
                            // the text should now offer the option to show the subcategories again
                            $el.text('Show subcategories');
                        } else {
                            // the subcategories are not currently visible
                            // So we show them by reinstating the flexbox
                            $childContainer.css('display', 'flex');
                            // and we give the option to hide them again
                            $el.text('Hide subcategories');
                        }
                    }}
                    className={styles.subcategoryToggle}
                    variant={TypographyVariants.SUBTEXT}
                >
                    Show subcategories
                </Typography>
            </div>
        );

        let [cardImage, cardName, onClick] = ReportService.getCardDetails(
            adjustments,
            group,
            this.props.drawerProps.cards,
            this.props.drawerProps.history
        );

        if (this.props.drawerProps.period === TimePeriod.PRESENT && !DateUtils.isLastMonthOfYear()) {
            const toDate: string = FormatService.format(totalAmount);
            const remainingAmount: string = FormatService.format(group.amount - totalAmount);
            toDateRow = (
                <div className={styles.subRow2} data-cy={Selectors.PlanAhead.SUB_ROW}>
                    <div>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('IncomeExpenseDrawer.toDate')}: {toDate}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('IncomeExpenseDrawer.projected')}: {remainingAmount}
                        </Typography>
                    </div>
                </div>
            );
        }

        if (!onClick) {
            onClick =
                this.props.drawerProps.period !== TimePeriod.FUTURE
                    ? () => {
                          void NavigationService.goTo(
                              this.props.drawerProps.history,
                              `/drawer/transactions?start=${this.props.drawerProps.startDate}&end=${this.props.drawerProps.endDate}&group=${group.accountId}`,
                              'Transactions Drawer'
                          );
                      }
                    : null;
        }

        const title: string = cardName || group.name;
        colorMap = ReportService.updateColorMapForTitle(colorMap, title);

        return (
            <ReportBar
                title={title}
                key={key}
                value={FormatService.formatCurrency(group.amount)}
                percentage={FormatService.formatPercentage(group.percentage)}
                color={colorMap.get(title)}
                img={cardImage}
                onClick={onClick}
            >
                {this.getBudgetRow(relevantBudget)}
                {this.getExpenseBarChildren(group.children, colorMap.get(title), childContainerClass)}
                {childToggle}
                {toDateRow}
            </ReportBar>
        );
    }

    private getExpenseBars(): JSX.Element[] {
        const groupedExpenses: AdjustmentModel[] = _.get(this, 'props.drawerProps.groupedExpenses', []);
        const expenses: ExpensesModel = _.get(this, 'props.drawerProps.cashFlowDetails.expenses');
        const expenseTotal: number = _.get(this, 'props.drawerProps.expenseTotal', 0);
        let baseExpenses: BaseExpenseModel[] = _.get(expenses, 'bases');
        let remainings: BaseExpenseModel[];
        if (this.props.drawerProps.period === TimePeriod.PRESENT) {
            remainings = _.orderBy(
                _.filter(baseExpenses, (base: BaseExpenseModel) => !base.actual),
                'amount',
                'desc'
            );
            baseExpenses = baseExpenses?.filter((base: BaseExpenseModel) => base.actual);
        }
        let bases: BaseExpenseModel[] = [];
        if (baseExpenses?.length === 0 && remainings) {
            for (const remaining of remainings) {
                const base: BaseExpenseModel = {
                    actual: true,
                    amount: 0,
                    percentage: 0,
                    groupUid: remaining.groupUid,
                    name: remaining.name,
                    id: remaining.id
                };
                bases.push(base);
            }
        } else {
            bases = baseExpenses?.map((base: BaseExpenseModel) => {
                base.percentage = (base.amount / expenses.amount) * 100;
                return base;
            });
        }

        const groups: AdjustmentModel[] = groupedExpenses?.map((group: AdjustmentModel) => {
            group.percentage = (group.amount / expenseTotal) * 100;
            if (group.children) {
                group.children = group.children.map((child: any) => {
                    child.percentage = (child.amount / expenseTotal) * 100;
                    return child;
                });
            }
            if (this.props.drawerProps.period === TimePeriod.PRESENT) {
                for (const b of bases) {
                    const isInGroup = !!group.children.find((t: BaseExpenseModel) => t.groupUid === b.groupUid);
                    if (!isInGroup) continue;
                    const alreadyIn = !!group.children.find((t: BaseExpenseModel) => t.id === b.id);
                    if (alreadyIn) continue;
                    group.children.push(b);
                }
            }
            group.accountId = String(group.id);
            return group;
        });

        return groups?.map((group: AdjustmentModel) => this.getExpenseBar(group, expenses.adjustments));
    }

    private getIncomeBar(
        base: BaseIncomeModel,
        remainings: BaseIncomeModel[],
        index: number,
        adjustments?: AdjustmentModel[]
    ): JSX.Element {
        let colorMap: Map<string, string> = new Map<string, string>();
        const key: string = `income-bar-${index}`;
        let toDateRow: JSX.Element;
        let totalAmount: number = base.amount;
        let [cardImage, cardName, onClick] = ReportService.getCardDetails(
            adjustments,
            base,
            this.props.drawerProps.cards,
            this.props.drawerProps.history
        );

        if (this.props.drawerProps.period === TimePeriod.PRESENT && !DateUtils.isLastMonthOfYear()) {
            let toDate: string = '$0';
            let remainingAmount: string = FormatService.format(totalAmount);
            try {
                const remaining: BaseIncomeModel = remainings?.find(
                    (remaining: BaseIncomeModel) => remaining.incomeUid === base.incomeUid
                );
                if (remaining) {
                    totalAmount += Number(remaining.amount);
                    toDate = FormatService.format(base.amount);
                    remainingAmount = FormatService.format(remaining.amount);
                }
            } catch (e) {
                console.error(e);
            }

            toDateRow = (
                <div className={styles.subRow2} data-cy={Selectors.PlanAhead.SUB_ROW}>
                    <div>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('IncomeExpenseDrawer.toDate')}: {toDate}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('IncomeExpenseDrawer.projected')}: {remainingAmount}
                        </Typography>
                    </div>
                </div>
            );
        }

        if (!onClick) {
            onClick =
                this.props.drawerProps.period !== TimePeriod.FUTURE
                    ? () => {
                          void NavigationService.goTo(
                              this.props.drawerProps.history,
                              `/drawer/transactions?start=${this.props.drawerProps.startDate}&end=${this.props.drawerProps.endDate}&incomesource=${base.incomeUid}`,
                              'Transactions Drawer'
                          );
                      }
                    : null;
        }

        const title: string = cardName || base.name;
        colorMap = ReportService.updateColorMapForTitle(colorMap, title);

        return (
            <ReportBar
                title={title}
                key={key}
                value={FormatService.formatCurrency(totalAmount)}
                percentage={FormatService.formatPercentage(base.percentage)}
                color={colorMap.get(title)}
                img={cardImage}
                onClick={onClick}
            >
                {toDateRow}
            </ReportBar>
        );
    }

    private getIncomeBars(): JSX.Element[] {
        const incomes: IncomeReportModel = _.get(this, 'props.drawerProps.cashFlowDetails.incomes');
        const adjustments: AdjustmentModel[] = _.get(incomes, 'adjustments', []);
        let baseIncomes: BaseIncomeModel[] = _.get(incomes, 'bases', []);
        let remainings: BaseIncomeModel[];
        if (this.props.drawerProps.period === TimePeriod.PRESENT) {
            remainings = _.orderBy(
                _.filter(baseIncomes, (base: BaseIncomeModel) => !base.actual),
                'amount',
                'desc'
            );
            baseIncomes = baseIncomes?.filter((base: BaseIncomeModel) => base.actual);
        }

        let bases: BaseIncomeModel[] = [];
        if (baseIncomes?.length === 0 && remainings) {
            for (const remaining of remainings) {
                const base: BaseIncomeModel = {
                    actual: true,
                    amount: 0,
                    percentage: 0,
                    incomeUid: remaining.incomeUid,
                    name: remaining.name,
                    detail: remaining.detail
                };
                bases.push(base);
            }
        } else {
            bases = baseIncomes?.map((base: BaseIncomeModel) => {
                base.percentage = (base.amount / incomes.amount) * 100;
                return base;
            });
        }

        return bases?.map((base: BaseIncomeModel, index: number) =>
            this.getIncomeBar(base, remainings, index, adjustments)
        );
    }

    private getTopDeposits(expense: boolean = false): JSX.Element[] {
        const topItems: TransactionModel[] = expense
            ? _.get(this, 'props.drawerProps.topExpenses')
            : _.get(this, 'props.drawerProps.topDeposits');
        const accounts: AccountModel[] = _.get(this, 'props.drawerProps.accounts');
        if (_.isEmpty(topItems) || _.isEmpty(accounts)) return;

        return topItems.map((td: TransactionModel, index: number) => {
            const relevantAccount: AccountModel = accounts.find((acc: AccountModel) => acc.id === td.accountId);
            return (
                <TransactionOverview
                    transactionModel={td}
                    account={relevantAccount}
                    key={`top-${expense ? 'expense' : 'deposit'}-${index}`}
                    startDate={this.props.drawerProps.startDate}
                    endDate={this.props.drawerProps.endDate}
                    history={this.props.drawerProps.history}
                    expense={expense}
                />
            );
        });
    }

    private getBudgetRow(budget: BudgetModel): JSX.Element {
        if (!budget) return null;
        let expenseBarSubRow: JSX.Element;
        if (budget) {
            expenseBarSubRow = <BudgetCallout budget={budget} history={this.props.drawerProps.history} />;
        }
        return expenseBarSubRow;
    }

    private getSeeAll(expense: boolean): JSX.Element {
        return (
            <Anchor
                className={styles.reportSeeAll}
                data-cy={Selectors.PlanAhead.SEE_ALL_TRANSACTIONS}
                onClick={() => {
                    void NavigationService.goTo(
                        this.props.drawerProps.history,
                        `/drawer/transactions?start=${this.props.drawerProps.startDate}&end=${this.props.drawerProps.endDate}&type=TRANSACTION_INCOME`,
                        'Transactions Drawer'
                    );
                }}
                typographyProps={{ variant: TypographyVariants.SUBTEXT_BOLD }}
            >
                <IconActivity className={styles.iconActivity} />
                See all{' '}
                {this.props.drawerProps.zoom === 'month'
                    ? `${_.capitalize(
                          this.props.drawerProps.startDate.month()
                      )} ${this.props.drawerProps.startDate.year()}`
                    : this.props.drawerProps.startDate.year()}{' '}
                {`${expense ? 'expense' : 'income'} transactions`}
            </Anchor>
        );
    }
}
